import type { ReactNode } from 'react';
import { GoogleIcon } from '@src/components/icons/GoogleIcon';
import { TelegramIcon } from '@src/components/icons/TelegramIcon';
import { VkIcon } from '@src/components/icons/VkIcon';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './SocialButton.css';

export type SocialKind = 'google' | 'telegram' | 'vk';

export interface SocialButtonProps {
  kind: SocialKind;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Keep the control clickable so a toast can explain why it is blocked. */
  disabledHint?: string;
  testId?: string;
}

const ICONS: Record<SocialKind, ReactNode> = {
  google: <GoogleIcon />,
  telegram: <TelegramIcon />,
  vk: <VkIcon />,
};

export function SocialButton({
  kind,
  children,
  onClick,
  disabled,
  disabledHint,
  testId,
}: SocialButtonProps) {
  const blocked = Boolean(disabled && disabledHint);

  return (
    <button
      type="button"
      className={classes('social-button', `social-button--${kind}`)}
      onClick={onClick}
      disabled={disabled && !blocked}
      aria-disabled={blocked || undefined}
      {...testIdAttr(testId)}
    >
      <span className="social-button__icon" aria-hidden="true">
        {ICONS[kind]}
      </span>
      <Typography as="span" role="label/button-secondary" color="inherit" size="sm">
        {children}
      </Typography>
    </button>
  );
}

export interface SocialRowProps {
  children: ReactNode;
  columns?: 1 | 2;
  testId?: string;
}

export function SocialRow({ children, columns = 2, testId }: SocialRowProps) {
  return (
    <div
      className={classes('social-row', `social-row--${columns}`)}
      {...testIdAttr(testId)}
    >
      {children}
    </div>
  );
}
